import React, { useEffect, useState } from "react";
import {useNavigate, useParams} from "react-router";
import {
  useGetUserQuery,
  useUpdateUserMutation,
  useGetSubscriptionPlansQuery,
  useGetUserSubscriptionSessionsQuery,
  useWaiveChatSessionMutation,
  useUnwaiveChatSessionMutation,
  useResetSubscriptionSessionsMutation
} from "../../../store/reducers/adminApi";
import Image from "react-bootstrap/Image";
import Badge from "react-bootstrap/Badge";
import Table from "react-bootstrap/Table";
import moment from "moment";
import { Link } from "react-router-dom";
import ToastBar, { ToastData } from "../../../components/ToastBar/toastbar";
import { Button, Form, InputGroup } from "react-bootstrap";

export function User() {
  const navigate = useNavigate();
  let { userID } = useParams();
  const [chatValue, setChatValue] = useState(0)
  const [subscriptionData, setSubscriptionData] = useState({
    has_subscription: false,
    subscription_type: '',
    subscription_start_date: '',
    subscription_end_date: ''
  })
  const {
    data: user,
    refetch,
  } = useGetUserQuery(userID, { refetchOnFocus: true, refetchOnMountOrArgChange: true });

  const [updateUser] = useUpdateUserMutation();
  const { data: subscriptionPlans } = useGetSubscriptionPlansQuery();
  const { data: subscriptionSessions, refetch: refetchSessions } = useGetUserSubscriptionSessionsQuery(userID);
  const [waiveSession] = useWaiveChatSessionMutation();
  const [unwaiveSession] = useUnwaiveChatSessionMutation();
  const [resetSessions, { isLoading: isResetting }] = useResetSubscriptionSessionsMutation();

  const [toastMessage, setToastMessage] = useState<ToastData>({
    title: null,
    body: null,
    type: null
  })

  const showToast = (title: string, body: string, type: 'success' | 'danger' | null) => {
    setToastMessage({
      title,
      body,
      type
    })
  }

  const handleWaiveSession = async (sessionId: string, isWaived: boolean) => {
    try {
      if (isWaived) {
        await unwaiveSession(sessionId).unwrap();
        showToast('Success', 'Session unwaived successfully', 'success');
      } else {
        await waiveSession(sessionId).unwrap();
        showToast('Success', 'Session waived successfully', 'success');
      }
      refetch();
      refetchSessions();
    } catch (error) {
      showToast('Error', 'Failed to update session', 'danger');
    }
  }

  const handleResetSessions = async () => {
    if (!window.confirm('Are you sure you want to reset all subscription sessions for this user? This action cannot be undone.')) {
      return;
    }
    try {
      const result = await resetSessions({ userId: userID!, reason: 'Admin dashboard reset' }).unwrap();
      showToast('Success', `Reset ${result.deleted_count} session(s) successfully`, 'success');
      refetch();
      refetchSessions();
    } catch (error: any) {
      showToast('Error', error?.data?.error || 'Failed to reset sessions', 'danger');
    }
  }

  const toggleEnable = () => {
    updateUser({ userID: user.userID, enabled: !user.enabled }).unwrap().then(() => {
      refetch()
      showToast('Action', `User ${user.enabled ? 'disabled' : 'enabled'} successfully`, 'success')
    }).catch((e) => {
      showToast('Error', e?.message || 'Something went wrong!', 'danger')
    });
  };

  const updateChatRate = () => {
    updateUser({ userID: user.userID, chat_value_available: (chatValue * 100).toString() }).unwrap().then(() => {
      refetch()
      showToast('Action', `Chat value updated successfully!`, 'success')
    }).catch((e) => {
      showToast('Error', e?.message || 'Something went wrong!', 'danger')
    });
  };
  
  const updateSubscription = () => {
    const updateData: any = {
      userID: user.userID,
      has_subscription: subscriptionData.has_subscription,
      subscription_type: subscriptionData.subscription_type
    };
    
    // Convert date strings to Date objects if they exist
    if (subscriptionData.subscription_start_date) {
      updateData.subscription_start_date = new Date(subscriptionData.subscription_start_date);
    }
    if (subscriptionData.subscription_end_date) {
      updateData.subscription_end_date = new Date(subscriptionData.subscription_end_date);
    }
    
    updateUser(updateData).unwrap().then(() => {
      refetch()
      showToast('Subscription', `User subscription ${subscriptionData.has_subscription ? 'added' : 'removed'} successfully!`, 'success')
    }).catch((e) => {
      showToast('Error', e?.message || 'Something went wrong!', 'danger')
    });
  };


  useEffect(()=>{
    const value = parseFloat(user?.chat_value_available || "0");
    setChatValue( value ? value / 100 : value )
    
    // Initialize subscription data
    if (user) {
      setSubscriptionData({
        has_subscription: user.has_subscription || false,
        subscription_type: user.subscription_type || '',
        subscription_start_date: user.subscription_start_date ? moment(user.subscription_start_date).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD'),
        subscription_end_date: user.subscription_end_date ? moment(user.subscription_end_date).format('YYYY-MM-DD') : ''
      })
    }
  }, [user]);

  console.log('User', user);


  let totalSpend = 0;
  if (user?.payments) {
    user.payments.forEach((payment) => {
      totalSpend += payment.charge_amount;
    });
  }

  return (
      <div>
        {user && (
            <React.Fragment>
              <Image roundedCircle src={user.avatar} width={150} height={150} />
              <Table>
                <tbody>
                <tr>
                  <td>Status</td>
                  <td>
                    <Badge bg={user.enabled ? "success" : "danger"}>
                      {user.enabled ? "enabled" : "disabled"}
                    </Badge>
                  </td>
                </tr>
                <tr>
                  <td>Total Spend</td>
                  <td>
                      ${(totalSpend / 100).toFixed(2)}
                  </td>
                </tr>
                <tr>
                  <td>Email</td>
                  <td>{user.email}</td>
                </tr>
                <tr>
                  <td>Username</td>
                  <td>{user.username}</td>
                </tr>
                <tr>
                  <td>Created On</td>
                  <td>{moment(user.created_at).format("MM-DD-YYYY HH:mm:ss")}</td>
                </tr>
                <tr>
                  <td>User Role</td>
                  <td>
                    <Badge bg={user.user_role === 'psychic' ? 'primary' : 'secondary'}>
                      {user.user_role === 'psychic' ? 'Advisor/Reader' : user.user_role === 'admin' ? 'Admin' : 'Seeker'}
                    </Badge>
                  </td>
                </tr>
                <tr>
                  <td>Action</td>
                  <td>
                    <Button size="sm" variant={user.enabled ? "danger" : "success"} onClick={toggleEnable}>
                      {user.enabled ? "Disable" : "Enable"} User
                    </Button>
                  </td>
                </tr>
                <tr>
                  <td>Chat Credit</td>
                  <td>
                    <InputGroup className="w-50">
                      <InputGroup.Text>$</InputGroup.Text>
                      <Form.Control
                          placeholder="Chat credit"
                          type="number"
                          min="0"
                          value={chatValue}
                          onChange={(e)=> setChatValue(parseFloat(e.target.value))}
                      />
                      <Button variant="outline-primary" disabled={chatValue !== 0 && !Number(chatValue)} onClick={updateChatRate}>
                        Update
                      </Button>
                    </InputGroup>
                  </td>
                </tr>
                </tbody>
              </Table>

              {/* Advisor Settings - Only show for psychics */}
              {user.user_role === 'psychic' && (
                <>
                  <h5>Advisor Settings</h5>
                  <Table>
                    <tbody>
                      <tr>
                        <td>Approved</td>
                        <td>
                          <Form.Check
                            type="switch"
                            id="approved-switch"
                            label={user.approved ? "Approved" : "Not Approved"}
                            checked={user.approved}
                            onChange={() => {
                              updateUser({ userID: user.userID, approved: !user.approved }).unwrap().then(() => {
                                refetch();
                                showToast('Action', `Advisor ${user.approved ? 'unapproved' : 'approved'} successfully`, 'success');
                              }).catch((e) => {
                                showToast('Error', e?.message || 'Something went wrong!', 'danger');
                              });
                            }}
                          />
                          <Form.Text className="text-muted">
                            Advisor must be approved to appear in listings and take sessions
                          </Form.Text>
                        </td>
                      </tr>
                      <tr>
                        <td>Stripe Active</td>
                        <td>
                          <Form.Check
                            type="switch"
                            id="stripe-active-switch"
                            label={user.stripe_active ? "Active" : "Inactive"}
                            checked={user.stripe_active}
                            onChange={() => {
                              updateUser({ userID: user.userID, stripe_active: !user.stripe_active }).unwrap().then(() => {
                                refetch();
                                showToast('Action', `Stripe ${user.stripe_active ? 'deactivated' : 'activated'} successfully`, 'success');
                              }).catch((e) => {
                                showToast('Error', e?.message || 'Something went wrong!', 'danger');
                              });
                            }}
                          />
                          <Form.Text className="text-muted">
                            Payment processing status for this advisor
                          </Form.Text>
                        </td>
                      </tr>
                      <tr>
                        <td>Subscription Only</td>
                        <td>
                          <Form.Check
                            type="switch"
                            id="subscription-only-switch"
                            label={user.subscription_only ? "Yes" : "No"}
                            checked={user.subscription_only}
                            onChange={() => {
                              updateUser({ userID: user.userID, subscription_only: !user.subscription_only }).unwrap().then(() => {
                                refetch();
                                showToast('Action', `Subscription only ${user.subscription_only ? 'disabled' : 'enabled'} successfully`, 'success');
                              }).catch((e) => {
                                showToast('Error', e?.message || 'Something went wrong!', 'danger');
                              });
                            }}
                          />
                          <Form.Text className="text-muted">
                            If enabled, only subscription users can chat with this advisor
                          </Form.Text>
                        </td>
                      </tr>
                      <tr>
                        <td>Available</td>
                        <td>
                          <Form.Check
                            type="switch"
                            id="available-switch"
                            label={user.available ? "Online" : "Offline"}
                            checked={user.available}
                            onChange={() => {
                              updateUser({ userID: user.userID, available: !user.available }).unwrap().then(() => {
                                refetch();
                                showToast('Action', `Availability ${user.available ? 'set to offline' : 'set to online'} successfully`, 'success');
                              }).catch((e) => {
                                showToast('Error', e?.message || 'Something went wrong!', 'danger');
                              });
                            }}
                          />
                          <Form.Text className="text-muted">
                            Whether the advisor is currently accepting sessions
                          </Form.Text>
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                </>
              )}

              <h5>Subscription Management</h5>
              {user.has_subscription && (
                <div className="mb-3">
                  <Badge bg={user.subscription_status === 'active' ? 'success' : 'warning'}>
                    Current Status: {user.subscription_status || 'Unknown'}
                  </Badge>
                  {user.subscription_end_date && (
                    <span className="ms-2">
                      Expires: {moment(user.subscription_end_date).format("MM-DD-YYYY")}
                    </span>
                  )}
                </div>
              )}
              <Table>
                <tbody>
                  <tr>
                    <td>Has Subscription</td>
                    <td>
                      <Form.Check
                        type="checkbox"
                        label={subscriptionData.has_subscription ? "Subscribed" : "Not Subscribed"}
                        checked={subscriptionData.has_subscription}
                        onChange={(e) => setSubscriptionData({...subscriptionData, has_subscription: e.target.checked})}
                      />
                    </td>
                  </tr>
                  {subscriptionData.has_subscription && (
                    <>
                      <tr>
                        <td>Subscription Type</td>
                        <td>
                          <Form.Select
                            value={subscriptionData.subscription_type}
                            onChange={(e) => setSubscriptionData({...subscriptionData, subscription_type: e.target.value})}
                            className="w-50"
                          >
                            <option value="">Select subscription type</option>
                            {subscriptionPlans?.map((plan) => (
                              <option key={plan.id} value={plan.name}>
                                {plan.name} - ${plan.amount / 100}/{plan.interval}
                              </option>
                            ))}
                          </Form.Select>
                        </td>
                      </tr>
                      <tr>
                        <td>Start Date</td>
                        <td>
                          <Form.Control
                            type="date"
                            value={subscriptionData.subscription_start_date}
                            onChange={(e) => setSubscriptionData({...subscriptionData, subscription_start_date: e.target.value})}
                            className="w-50"
                          />
                        </td>
                      </tr>
                      <tr>
                        <td>End Date</td>
                        <td>
                          <Form.Control
                            type="date"
                            value={subscriptionData.subscription_end_date}
                            onChange={(e) => setSubscriptionData({...subscriptionData, subscription_end_date: e.target.value})}
                            className="w-50"
                          />
                        </td>
                      </tr>
                    </>
                  )}
                  <tr>
                    <td>Action</td>
                    <td>
                      <Button size="sm" variant="primary" onClick={updateSubscription}>
                        Update Subscription
                      </Button>
                    </td>
                  </tr>
                </tbody>
              </Table>
              
              <h5>Payments</h5>
              <Table>
                <thead>
                <tr>
                  <th>Date</th>
                  <th>Charge Amount</th>
                  <th>Session Value</th>
                  <th>Chat Rate</th>
                  <th>Chat</th>
                </tr>
                </thead>
                <tbody>
                {user.payments.map((payment) => {
                  return (
                      <tr key={payment.paymentID}>
                        <td>{moment(payment.created_at).format("MM-DD-YYYY HH:mm:ss")}</td>
                        <td>${(payment.charge_amount / 100).toFixed(2)}</td>
                        <td>${(payment.value / 100).toFixed(2)}</td>
                        <td>${(payment.chat_rate / 100).toFixed(2)}/min</td>
                        <td>
                          <Link to={`/chat/${payment.chatSession?.chatID}`}>
                            {payment.chatSession?.chatID}
                          </Link>
                        </td>
                      </tr>
                  );
                })}
                </tbody>
              </Table>


              {user.chatRequestsReceived && Array.isArray(user.chatRequestsReceived) && user.chatRequestsReceived.length > 0 ? <><h5>Chat Requests Received</h5>
                <Table>
                  <thead>
                  <tr>
                    <th>Date</th>
                    <th>Psychic</th>
                    <th>Status</th>
                  </tr>
                  </thead>
                  <tbody>
                  {user.chatRequestsReceived.map((chatRequest) => {
                    return (
                        <tr key={chatRequest.chatRequestID}>
                          <td>{moment(chatRequest.created_at).format("MM-DD-YYYY HH:mm:ss")}</td>
                          <td>{chatRequest.to_user?.name}</td>
                          <td>{chatRequest.status}</td>
                        </tr>
                    );
                  })}
                  </tbody>
                </Table></> : null}

              {user.chatRequestsSent && Array.isArray(user.chatRequestsSent) && user.chatRequestsSent.length > 0 ? <><h5>Chat Requests Sent</h5>
                <Table>
                  <thead>
                  <tr>
                    <th>Date</th>
                    <th>Psychic</th>
                    <th>Status</th>
                  </tr>
                  </thead>
                  <tbody>
                  {user.chatRequestsSent.map((chatRequest) => {
                    return (
                        <tr key={chatRequest.chatRequestID}>
                          <td>{moment(chatRequest.created_at).format("MM-DD-YYYY HH:mm:ss")}</td>
                          <td>
                            <a href={`/psychic/${chatRequest.to_user_id}`}>
                              {chatRequest.to_user_id}
                            </a>
                          </td>
                          <td>{chatRequest.status}</td>
                        </tr>
                    );
                  })}
                  </tbody>
                </Table></> : null}

              {user.chats && Array.isArray(user.chats) && user.chats.length > 0 ? <><h5>Chats</h5>
                <Table>
                  <thead>
                  <tr>
                    <th>Date</th>
                    <th>Status</th>
                  </tr>
                  </thead>
                  <tbody>
                  {user.chats.map((chat) => {
                    return (
                        <tr key={chat?.chatID}>
                          <td>
                            <a href={`/chat/${chat?.chatID}`}>
                              {moment(chat.created_at).format("MM-DD-YYYY HH:mm:ss")}
                            </a>
                          </td>
                          <td>{chat.status}</td>
                        </tr>
                    );
                  })}
                  </tbody>
                </Table></> : null}

              {/* Subscription Sessions */}
              {subscriptionSessions && (
                <>
                  {subscriptionSessions.currentLimit && (
                    <div className="alert alert-info mb-3">
                      <h5>Subscription Session Usage</h5>
                      <strong>Sessions Used:</strong> {subscriptionSessions.currentLimit.sessions_used} of {subscriptionSessions.currentLimit.session_limit}
                      {subscriptionSessions.currentLimit.billing_period_end && (
                        <span> (resets {moment(subscriptionSessions.currentLimit.billing_period_end).format("MM/DD/YYYY")})</span>
                      )}
                    </div>
                  )}
                  
                  {subscriptionSessions.usage && subscriptionSessions.usage.length > 0 && (
                    <>
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <h5 className="mb-0">Subscription Sessions</h5>
                        <Button
                          size="sm"
                          variant="outline-danger"
                          onClick={handleResetSessions}
                          disabled={isResetting}
                        >
                          {isResetting ? 'Resetting...' : 'Reset All Sessions'}
                        </Button>
                      </div>
                      <Table striped bordered hover>
                        <thead>
                          <tr>
                            <th>Date</th>
                            <th>Session ID</th>
                            <th>Status</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {subscriptionSessions.usage.map((session: any, index: number) => (
                            <tr key={session.chat_session_id || `session-${index}`}>
                              <td>{moment(session.created_at).format("MM/DD/YY HH:mm")}</td>
                              <td>{session.chat_session_id ? `${session.chat_session_id.substring(0, 8)}...` : <span className="text-muted">N/A</span>}</td>
                              <td>
                                <Badge bg={session.is_waived ? "warning" : "success"}>
                                  {session.is_waived ? "Waived" : "Counted"}
                                </Badge>
                              </td>
                              <td>
                                <Button
                                  size="sm"
                                  variant={session.is_waived ? "warning" : "success"}
                                  onClick={() => handleWaiveSession(session.chat_session_id, session.is_waived)}
                                  disabled={!session.chat_session_id}
                                  title={!session.chat_session_id ? "Cannot waive: missing session ID" : undefined}
                                >
                                  {session.is_waived ? "Unwaive" : "Waive"}
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </>
                  )}
                </>
              )}
            </React.Fragment>
        )}
        <ToastBar tostData={toastMessage} setToastData={setToastMessage} />
      </div>
  );
}
